import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Upload, ChevronLeft, ChevronRight, FolderOpen, Image as ImageIcon, Home } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MediaLibrary = ({ open, onClose, onSelect, getToken }) => {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [currentPath, setCurrentPath] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchMedia = useCallback(async (pg = 1, path = '') => {
    setLoading(true);
    try {
      const token = getToken ? getToken() : null;
      const params = new URLSearchParams({ page: pg, limit: 40 });
      if (path) params.set('path', path);
      const res = await axios.get(`${API_URL}/api/admin/media?${params}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setItems(res.data.items);
      setFolders(res.data.folders);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
      setPage(pg);
      setCurrentPath(res.data.current_path || '');
    } catch (err) {
      console.error('Media fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (open) fetchMedia(1, '');
  }, [open, fetchMedia]);

  const navigateToFolder = (folderPath) => {
    fetchMedia(1, folderPath);
  };

  const navigateUp = () => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    fetchMedia(1, parts.join('/'));
  };

  const breadcrumbs = currentPath ? currentPath.split('/').filter(Boolean) : [];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getToken ? getToken() : null;
      const res = await axios.post(`${API_URL}/api/admin/media/upload-gcs`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      onSelect(res.data.url);
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="media-library-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-3">
            <ImageIcon size={20} className="text-[#9B1E37]" />
            <h3 className="font-semibold text-lg">Media Library</h3>
            <span className="text-sm text-slate-400">{total} images{folders.length > 0 ? `, ${folders.length} folders` : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
              <Button variant="outline" size="sm" asChild disabled={uploading}>
                <span>
                  {uploading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
                  Upload to GCS
                </span>
              </Button>
            </label>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-1 px-5 py-2 border-b bg-slate-50 text-sm overflow-x-auto">
          <button
            onClick={() => fetchMedia(1, '')}
            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-200 text-slate-600 shrink-0"
          >
            <Home size={14} />
            Root
          </button>
          {breadcrumbs.map((crumb, i) => {
            const path = breadcrumbs.slice(0, i + 1).join('/');
            const isLast = i === breadcrumbs.length - 1;
            return (
              <span key={path} className="flex items-center gap-1 shrink-0">
                <span className="text-slate-300">/</span>
                <button
                  onClick={() => !isLast && navigateToFolder(path)}
                  className={`px-2 py-1 rounded ${isLast ? 'font-medium text-[#9B1E37]' : 'hover:bg-slate-200 text-slate-600'}`}
                >
                  {crumb}
                </button>
              </span>
            );
          })}
          {currentPath && (
            <button onClick={navigateUp} className="ml-auto px-2 py-1 rounded hover:bg-slate-200 text-slate-500 text-xs shrink-0">
              Back
            </button>
          )}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 size={32} className="animate-spin text-slate-300" />
            </div>
          ) : (
            <>
              {/* Folders */}
              {folders.length > 0 && (
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 mb-4">
                  {folders.map((folder) => (
                    <button
                      key={folder.path}
                      onClick={() => navigateToFolder(folder.path)}
                      className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-slate-100 transition-colors group"
                      title={folder.name}
                    >
                      <FolderOpen size={28} className="text-[#F6BF58] group-hover:text-[#9B1E37] transition-colors" />
                      <span className="text-xs text-slate-600 truncate w-full text-center">{folder.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Files */}
              {items.length === 0 && folders.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-slate-400">No images in this folder</div>
              ) : items.length > 0 ? (
                <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {items.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => { onSelect(item.url); onClose(); }}
                      className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-[#9B1E37] transition-colors group relative"
                      title={item.name}
                    >
                      <img
                        src={item.url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-5 py-3 border-t">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchMedia(page - 1, currentPath)}>
              <ChevronLeft size={14} />
            </Button>
            <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => fetchMedia(page + 1, currentPath)}>
              <ChevronRight size={14} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaLibrary;
